import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ModeContextProps } from './Contexts/ModeContext';
import ModeContext from './Contexts/ModeContext';
import Mode from './Enums/Mode';
import ConnectPage from './Pages/ConnectPage';
import { ThemeProvider } from '@mui/material/styles';
import type { SettingsContextProps } from './Contexts/SettingsContext';
import SettingsContext from './Contexts/SettingsContext';
import darkModeTheme from './Themes/DarkModeTheme';
import lightModeTheme from './Themes/LightModeTheme';
import AppTheme from './Enums/AppTheme';
import PageWrapperComponent from './Components/PageWrapper/PageWrapperComponent';
import type { JSX } from 'react';
import ApiService from './Services/ApiService';
import LocalStorageService from './Services/LocalStorageService';
import type { LocalStorageContextProps } from './Contexts/LocalStorageContext';
import LocalStorageContext from './Contexts/LocalStorageContext';
import type CurrentUser from './Models/CurrentUserModel';
import type SystemInfo from './Models/SystemInfoModel';
import type AppConfig from './Models/AppConfigModel';
import { appVersion, minApiVersion } from './Constants';
import ErrorPage from './Pages/ErrorPage';
import type { ApiContextProps } from './Contexts/ApiContext';
import ApiContext from './Contexts/ApiContext';
import type { SystemContextProps } from './Contexts/SystemContext';
import SystemContext from './Contexts/SystemContext';
import type { UserContextProps } from './Contexts/UserContext';
import UserContext from './Contexts/UserContext';
import type { TokenContextProps } from './Contexts/TokenContext';
import TokenContext from './Contexts/TokenContext';
import type TenantToken from './Models/TenantTokenModel';
import type KFleetToken from './Models/KFleetTokenModel';
import TokenType from './Enums/TokenType';
import MainPageRouter from './Routers/MainPageRouter';
import { checkVersion } from 'shouldve-been-js';
import type BugReport from './Models/BugReportModel';
import type { BugReportContextProps } from './Contexts/BugReportContext';
import BugReportContext from './Contexts/BugReportContext';

function App(): JSX.Element {
   
    const [mode, setMode] = useState<Mode>(Mode.CONNECT);
    const [theme, setTheme] = useState<AppTheme>(AppTheme.LIGHT_MODE);
    const [api, setApi] = useState<ApiService | null>(null);
    const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
    const [lastActivity, setLastActivity] = useState<Date | null>(null);
    const [health, setHealth] = useState<boolean>(false);

    const [systemName, setSystemName] = useState<string | null>(null);
    const [apiVersion, setApiVersion] = useState<string | null>(null);
    const [isOffice, setIsOffice] = useState<boolean>(false);
    const [minAppVersion, setMinAppVersion] = useState<string | null>(null);
    const [appEnabled, setAppEnabled] = useState<boolean>(false);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [serverTimezoneOffsetMinutes, setServerTimezoneOffsetMinutes] = useState<number | null>(null);
    const [tenantDisplayName, setTenantDisplayName] = useState<string | null>(null);

    const [token, setToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [expiration, setExpiration] = useState<Date | null>(null);
    const [tokenType, setTokenType] = useState<TokenType>(TokenType.TENANT);

    const [bugReports, setBugReports] = useState<BugReport[]>([]);

    const logoObjectUrlRef = useRef<string | null>(null);

    //used to prevent error messages from showing before the application is initialized
    const [initializedFromLocalStorage, setInitializedFromLocalStorage] = useState<boolean>(false);
    const [connectedDataFetched, setConnectedDataFetched] = useState<boolean>(false);

    const [errorMessageClient, setErrorMessageClient] = useState<string | null>(null);
    const [errorMessageSystem, setErrorMessageSystem] = useState<string | null>(null);
    
    const [refreshConnectedDataRerenderFlag, setRefreshConnectedDataRerenderFlag] = useState<Date>(new Date());
    
    const connected: boolean = mode !== Mode.CONNECT;
    
    const localStorageService: LocalStorageService = useMemo<LocalStorageService>(() => new LocalStorageService('purchase'), []);
    
    const addBugReport: (newBugReport: BugReport) => void = useCallback((newBugReport: BugReport) => {
        
        setBugReports((prev: BugReport[]) => [...prev, newBugReport]);
    }, []);

    const modeContextValues: ModeContextProps = useMemo<ModeContextProps>(() => {
        return {
            mode: mode,
            setMode: setMode,
        };
    }, [mode]);
    
    const userContextValues: UserContextProps = useMemo<UserContextProps>(() => {
        return {
            currentUser: currentUser,
            setCurrentUser: setCurrentUser,
            lastActivity: lastActivity,
            setLastActivity: setLastActivity,
        };
    }, [currentUser, lastActivity]);
    
    const tokenContextValues: TokenContextProps = useMemo<TokenContextProps>(() => {
        return {
            token: token,
            setToken: setToken,
            refreshToken: refreshToken,
            setRefreshToken: setRefreshToken,
            expiration: expiration,
            setExpiration: setExpiration,
            tokenType: tokenType,
            setTokenType: setTokenType,
        };
    }, [expiration, refreshToken, token, tokenType]);
    
    const apiContextValues: ApiContextProps = useMemo<ApiContextProps>(() => {
        return {
            api: api,
            setApi: setApi,
            setRefreshConnectedDataRerenderFlag: setRefreshConnectedDataRerenderFlag,
        };
    }, [api]);
    
    const localStorageContextValues: LocalStorageContextProps = useMemo<LocalStorageContextProps>(() => {
        return {
            localStorageService: localStorageService,
        };
    }, [localStorageService]);
    
    const bugReportContextValues: BugReportContextProps = useMemo<BugReportContextProps>(() => {
        return {
            bugReports: bugReports,
            setBugReports: setBugReports,
            addBugReport: addBugReport,
        };
    }, [addBugReport, bugReports]);
    
    const settingsContextValues: SettingsContextProps = useMemo<SettingsContextProps>(() => {
        return {
            theme: theme,
            setTheme: setTheme,
        };
    }, [theme]);
    
    const systemContextValues: SystemContextProps = useMemo<SystemContextProps>(() => {
        return {
            systemName: systemName,
            setSystemName: setSystemName,
            apiVersion: apiVersion,
            setApiVersion: setApiVersion,
            isOffice: isOffice,
            setIsOffice: setIsOffice,
            minAppVersion: minAppVersion,
            setMinAppVersion: setMinAppVersion,
            appEnabled: appEnabled,
            setAppEnabled: setAppEnabled,
            logoUrl: logoUrl,
            setLogoUrl: setLogoUrl,
            serverTimezoneOffsetMinutes: serverTimezoneOffsetMinutes,
            setServerTimezoneOffsetMinutes: setServerTimezoneOffsetMinutes,
            tenantDisplayName: tenantDisplayName,
            setTenantDisplayName: setTenantDisplayName,
        };
    }, [apiVersion, appEnabled, isOffice, logoUrl, minAppVersion, serverTimezoneOffsetMinutes, systemName, tenantDisplayName]);

    const renderBody: JSX.Element = useMemo<JSX.Element>(() => {
        
        if (errorMessageClient !== null) {
            return (
                <ErrorPage 
                    errorMessage={errorMessageClient}
                    canDisconnect={false}
                />
            );
            
        } else if (mode === Mode.CONNECT) {
            return <ConnectPage />; 

        } else if (errorMessageSystem !== null) {
            return (
                <ErrorPage 
                    errorMessage={errorMessageSystem}
                    canDisconnect={true}
                />
            );

        } else if (mode === Mode.MAIN) {
            return <MainPageRouter />;

        } else { //should not happen
            return <ConnectPage />;
        }
    }, [errorMessageClient, errorMessageSystem, mode]);

    useEffect(() => { //check health

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time

        async function checkHealth(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                if (connected) {
                    const newHealth: boolean = await api?.getHealth(token) ?? false;

                    if (!mounted) {
                        return;
                    }

                    if (newHealth) {
                        setHealth(true);
                        return;
                    }

                    //if fails, wait 2 seconds and try once more before doing anything
                    await new Promise(r => setTimeout(r, 2000));
                    const retryHealth: boolean = await api?.getHealth(token) ?? false;

                    if (!mounted) {
                        return;
                    }

                    setHealth(retryHealth);
                } else {
                    setHealth(true);
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        void checkHealth(); //run once immediately
        const interval: ReturnType<typeof setInterval> = setInterval(checkHealth, 10000); //run again every 10th second
         
        return () => {
            clearInterval(interval);
            mounted = false;
        };
    }, [api, connected, token]);

    useEffect(() => { //initialize values from local storage

        if (initializedFromLocalStorage) {
            return;
        }
     
        const newMode: Mode | null = localStorageService.getMode();
        const newTheme: AppTheme | null = localStorageService.getTheme();
        const newCurrentUser: CurrentUser | null = localStorageService.getCurrentUser();
        const newLastActivity: Date | null = localStorageService.getLastActivity();
        const newToken: string | null = localStorageService.getToken();
        const newRefreshToken: string | null = localStorageService.getRefreshToken();
        const newExpiration: Date | null = localStorageService.getExpiration();
        const newTokenType: TokenType = localStorageService.getTokenType();
        const newTenantDisplayName: string | null = localStorageService.getTenantDisplayName();
        const newApiBaseUrl: string | null = localStorageService.getApiBaseUrl();

        if (newMode !== null) {
            setMode(newMode);
        }

        if (newTheme !== null) {
            setTheme(newTheme);
            document.documentElement.setAttribute('data-bs-theme', newTheme === AppTheme.LIGHT_MODE ? 'light' : 'dark'); //for the embedded order html
        }

        if (newCurrentUser !== null) {
            setCurrentUser(newCurrentUser);
        }

        if (newLastActivity !== null) {
            setLastActivity(newLastActivity);
        }

        if (newToken !== null) {
            setToken(newToken);
        }

        if (newRefreshToken !== null) {
            setRefreshToken(newRefreshToken);
        }

        if (newExpiration !== null) { //could consider checking expiration here, and handling right away if it has expired
            setExpiration(newExpiration);
        }

        setTokenType(newTokenType);

        if (newTenantDisplayName !== null) {
            setTenantDisplayName(newTenantDisplayName);
        }

        if (newApiBaseUrl !== null) {
            setApi(new ApiService(newApiBaseUrl, addBugReport));
        }

        setInitializedFromLocalStorage(true);

    }, [addBugReport, initializedFromLocalStorage, localStorageService]);

    useEffect(() => { //fetch this info when connected (fast, most vital info)

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time

        async function fetchVitalConnectedData(): Promise<void> {

            if (api === null) {
                return;
            }

            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                const systemInfo: SystemInfo | null = await api.getSystemInfo(token);
                const appConfig: AppConfig | null = await api.getAppConfig(token);
                const logoBlob: Blob | null = await api.getCompanyLogo(token);
                const newServerTimezoneOffsetMinutes: number | null = await api.getServerTimezoneOffsetMinutes(token);
                
                if (mounted) {

                    if (systemInfo !== null) {
                        setSystemName(systemInfo.systemName);
                        setApiVersion(systemInfo.apiVersion);
                        setIsOffice(systemInfo.isOffice);
                    }

                    if (appConfig !== null) {
                        setMinAppVersion(appConfig.minAppVersion);
                        setAppEnabled(appConfig.appEnabled);
                    }

                    if (logoBlob !== null) {
                        const nextLogoUrl: string = URL.createObjectURL(logoBlob);

                        if (logoObjectUrlRef.current !== null) {
                            URL.revokeObjectURL(logoObjectUrlRef.current);
                        }

                        logoObjectUrlRef.current = nextLogoUrl;
                        setLogoUrl(nextLogoUrl);
                    }

                    if (newServerTimezoneOffsetMinutes !== null) {
                        setServerTimezoneOffsetMinutes(newServerTimezoneOffsetMinutes);
                    }

                    setConnectedDataFetched(true);
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        if (connected) {
            void fetchVitalConnectedData(); //run once immediately
            const interval: ReturnType<typeof setInterval> = setInterval(fetchVitalConnectedData, 300000); //run again every 5th minute
            
            return () => {

                if (logoObjectUrlRef.current !== null) {
                    URL.revokeObjectURL(logoObjectUrlRef.current);
                    logoObjectUrlRef.current = null;
                }

                clearInterval(interval);
                mounted = false;
            };
        } else {
            setConnectedDataFetched(false);
            return undefined;
        }
    }, [api, connected, localStorageService, token, refreshConnectedDataRerenderFlag]);

    useEffect(() => { //check if anything client related is wrong

        if (!LocalStorageService.isLocalStorageAvailable()) {
            setErrorMessageClient('Your browser does not support localstorage. This is required. Please try another browser.');
        } else if (crypto.subtle === undefined) {
            setErrorMessageClient('Your browser does not support crypto.subtle. This is required. Please try another browser.');
        } else {
            setErrorMessageClient(null);
        }
    }, []);

    useEffect(() => { //check if anything system related is wrong

        if (!initializedFromLocalStorage || !connectedDataFetched) { //not ready to check yet
            return;
        }

        if (connected) {

            if (!appEnabled) {
                setErrorMessageSystem('This system is not configured for using this app. Contact your system administrator.');
            } else if (!isOffice) {
                setErrorMessageSystem('You have connected to a vessel system. Only office systems are supported.');
            } else if (minAppVersion === null || appVersion === null || !checkVersion(appVersion, minAppVersion)) {
                setErrorMessageSystem('You are on app version ' + appVersion + ', while your system requires at least version ' + (minAppVersion ?? 'ERROR') + '. Close all other tabs of this application and restart it with internet connection to update.');
            } else if (minApiVersion === null || apiVersion === null || !checkVersion(apiVersion, minApiVersion)) {
                setErrorMessageSystem('You are connected to an API on version ' + (apiVersion ?? 'ERROR') + ', while the app requires at least version ' + minApiVersion + '. Contact your system administrator.');
            } else {
                setErrorMessageSystem(null);
            }
        } else {
            setErrorMessageSystem(null);
        }
    }, [apiVersion, appEnabled, connected, connectedDataFetched, initializedFromLocalStorage, isOffice, minAppVersion]);

    useEffect(() => { //check for token expiration

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time

        async function checkTokenExpiration(): Promise<void> {

            if (refreshToken === null || expiration === null || currentUser === null || !health) { //don't check if there is no token or no connection, the health check and dependency will also make this check be run every time connection is reestablished which is good
                return;
            }

            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                //currently you wont be logged out if the token just stops working (until the expiration is reached), this should normally not be a problem, and will be hard to detect from other failures when running through the tunnel, so should be fine
                if (expiration.getTime() < Date.now() + 2 * 60 * 1000) { //if token expires in less than two minutes

                    if (tokenType === TokenType.TENANT) {

                        const tokenInfo: TenantToken | null | undefined = await ApiService.fetchNewTenantTokenByRefreshToken(refreshToken, addBugReport);

                        if (mounted) {
                            if (tokenInfo === null) {

                                //refresh token has expired
                                //this code should reflect what is done in handleDisconnect, but is currently duplicated as the contexts are not available here                
                                setCurrentUser(null);
                                localStorageService.clearCurrentUser();
                                setLastActivity(null);
                                localStorageService.clearLastActivity();

                                setToken(null);
                                setRefreshToken(null);
                                setExpiration(null);
                                setTokenType(TokenType.TENANT);
                                localStorageService.clearToken();
                                localStorageService.clearRefreshToken();
                                localStorageService.clearExpiration();
                                localStorageService.clearTokenType();

                                setApi(null);
                                localStorageService.setApiBaseUrl(null);

                                setMode(Mode.CONNECT);
                                localStorageService.setMode(Mode.CONNECT);

                                setSystemName(null);
                                setApiVersion(null);
                                setIsOffice(false);
                                setMinAppVersion(null);
                                setAppEnabled(false);
                                setLogoUrl(null);
                                setServerTimezoneOffsetMinutes(null);
                                setTenantDisplayName(null);
                                localStorageService.clearTenantDisplayName();
                                return;
                            }

                            const newToken: string | null = tokenInfo?.token ?? null;
                            const newRefreshToken: string | null = tokenInfo?.refreshToken ?? null;
                            const newExpiration: Date | null = tokenInfo?.expiration ?? null;

                            if (newToken === null || newRefreshToken === null || newExpiration === null) {
                                return;
                            }

                            const newAccess: string[] = await api?.getUserAccess(currentUser.userId, newToken) ?? [];

                            const newCurrentUser: CurrentUser = currentUser.copy();
                            newCurrentUser.access = newAccess;

                            setCurrentUser(newCurrentUser);
                            localStorageService.setCurrentUser(newCurrentUser);

                            setToken(newToken);
                            setRefreshToken(newRefreshToken);
                            setExpiration(newExpiration);
                            localStorageService.setToken(newToken);
                            localStorageService.setRefreshToken(newRefreshToken);
                            localStorageService.setExpiration(newExpiration);
                        }
                    } else {

                        const tokenInfo: KFleetToken | null | undefined = await api?.fetchNewKFleetTokenByRefreshToken(refreshToken);

                        if (mounted) {
                            if (tokenInfo === null) {

                                //refresh token has expired
                                //this code should reflect what is done in handleDisconnect, but is currently duplicated as the contexts are not available here                
                                setCurrentUser(null);
                                localStorageService.clearCurrentUser();
                                setLastActivity(null);
                                localStorageService.clearLastActivity();

                                setToken(null);
                                setRefreshToken(null);
                                setExpiration(null);
                                setTokenType(TokenType.TENANT);
                                localStorageService.clearToken();
                                localStorageService.clearRefreshToken();
                                localStorageService.clearExpiration();
                                localStorageService.clearTokenType();

                                setApi(null);
                                localStorageService.setApiBaseUrl(null);

                                setMode(Mode.CONNECT);
                                localStorageService.setMode(Mode.CONNECT);

                                setSystemName(null);
                                setApiVersion(null);
                                setIsOffice(false);
                                setMinAppVersion(null);
                                setAppEnabled(false);
                                setLogoUrl(null);
                                setServerTimezoneOffsetMinutes(null);
                                setTenantDisplayName(null);
                                localStorageService.clearTenantDisplayName();
                                return;
                            }

                            const newToken: string | null = tokenInfo?.token ?? null;
                            const newRefreshToken: string | null = tokenInfo?.refreshToken ?? null;
                            const newAccess: string[] | null = tokenInfo?.access ?? null;
                            const newExpiration: Date | null = tokenInfo?.expiration ?? null;

                            if (newToken === null || newRefreshToken === null || newAccess === null || newExpiration === null) {
                                return;
                            }

                            const newCurrentUser: CurrentUser = currentUser.copy();
                            newCurrentUser.access = newAccess;

                            setCurrentUser(newCurrentUser);
                            localStorageService.setCurrentUser(newCurrentUser);

                            setToken(newToken);
                            setRefreshToken(newRefreshToken);
                            setExpiration(newExpiration);
                            localStorageService.setToken(newToken);
                            localStorageService.setRefreshToken(newRefreshToken);
                            localStorageService.setExpiration(newExpiration);
                        }
                    }
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        void checkTokenExpiration();
        const interval: ReturnType<typeof setInterval> = setInterval(checkTokenExpiration, 60000); //run again every minute

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [addBugReport, api, currentUser, expiration, health, localStorageService, refreshToken, token, tokenType]);

    return (
        <ModeContext.Provider value={modeContextValues}>
            <UserContext.Provider value={userContextValues}>
                <TokenContext.Provider value={tokenContextValues}>
                    <ApiContext.Provider value={apiContextValues}>
                        <LocalStorageContext.Provider value={localStorageContextValues}>
                            <BugReportContext.Provider value={bugReportContextValues}>
                                <SettingsContext.Provider value={settingsContextValues}>
                                    <SystemContext.Provider value={systemContextValues}>
                                        <ThemeProvider theme={theme === AppTheme.DARK_MODE ? darkModeTheme : lightModeTheme}>
                                            <PageWrapperComponent
                                                renderBody={renderBody}
                                            />
                                        </ThemeProvider>
                                    </SystemContext.Provider>
                                </SettingsContext.Provider>
                            </BugReportContext.Provider>
                        </LocalStorageContext.Provider>
                    </ApiContext.Provider>
                </TokenContext.Provider>
            </UserContext.Provider>
        </ModeContext.Provider>
    );
}

export default App;